import App from "@/App";
import ReactDOM from "react-dom/client";

/**
 * ResizeObserver loop 오류 임시 억제
 *
 * 주의: 이것은 임시방편입니다. 근본 원인은 Mantine AutoComplete 컴포넌트들이
 * 동시에 렌더링되면서 드롭다운 크기가 빠르게 변경되어 발생합니다.
 *
 * 적용된 최적화:
 * ✅ AutoComplete 컴포넌트들을 React.memo로 메모이제이션
 * ✅ 드롭다운 높이 고정 (CSS)
 * ✅ debounce를 통한 API 호출 최소화
 * ✅ lazy loading으로 드롭다운이 열릴 때만 데이터 로드
 *
 * TODO: 추가 개선 필요
 * - 프로덕션 환경에서 실제 발생 여부 모니터링 (Sentry 등)
 * - Mantine 라이브러리 업데이트 시 해당 이슈 해결 여부 확인
 *
 * 관련 파일:
 * - src/custom/features/ui/autoComplete/item/item-autoComplete.tsx
 * - src/features/purchaseOrderItems/form/UpdateForm.tsx
 */
const suppressResizeObserverError = (): void => {
  // 개발 환경에서만 억제 (프로덕션에서는 실제 문제를 모니터링)
  if (process.env.NODE_ENV !== "development") {
    return;
  }

  const resizeObserverErrRe = /ResizeObserver loop/;

  // 1. window.ResizeObserver를 래핑하여 에러를 내부에서 차단
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        requestAnimationFrame(() => {
          try {
            callback(entries, observer);
          } catch (error: any) {
            if (!resizeObserverErrRe.test(error?.message)) {
              throw error;
            }
            // ResizeObserver loop 에러는 무시
            console.debug("[Suppressed] ResizeObserver loop detected in callback");
          }
        });
      });
    }
  } as any;

  // 2. error 이벤트 차단
  window.addEventListener("error", (e: ErrorEvent) => {
    if (resizeObserverErrRe.test(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  });

  // 3. console.error 필터링 (중요한 다른 오류는 그대로 출력)
  const originalError = console.error.bind(console);
  console.error = (message?: any, ...optionalParams: any[]): void => {
    if (typeof message === "string" && resizeObserverErrRe.test(message)) {
      // 억제하지만 로그는 남김 (디버깅용)
      console.debug("[Suppressed] ResizeObserver loop detected in console.error");
      return;
    }
    originalError(message, ...optionalParams);
  };
};

suppressResizeObserverError();

ReactDOM.createRoot(document.getElementById("root")!).render(<App />);
